import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import PlanGate from "@/app/PlanGate";
import { PageHeader, Btn, Money, Spinner } from "@/app/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, LayoutGrid, List, GripVertical, Trash2 } from "lucide-react";

const SOURCE_STYLE = { Website: "bg-blue-50 text-blue-accent", Referral: "bg-emerald-50 text-emerald-700", Manual: "bg-slate-100 text-slate-600" };

function LeadsInner() {
  const { user } = useAuth();
  const sym = user?.business?.currency || "£";
  const { data, loading, refetch } = useFetch("/leads");
  const [view, setView] = useState("board");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", customer_name: "", value: 0, source: "Manual", notes: "" });
  const [drag, setDrag] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.title.trim() || !form.customer_name.trim()) return toast.error("Title and customer required");
    try {
      await api.post("/leads", { ...form, value: Number(form.value) });
      toast.success("Lead added"); setOpen(false);
      setForm({ title: "", customer_name: "", value: 0, source: "Manual", notes: "" }); refetch();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete lead?")) return; await api.delete(`/leads/${id}`); refetch(); };
  const drop = async (stage) => {
    if (!drag) return;
    await api.put(`/leads/${drag}/move`, { stage, order: 0 });
    setDrag(null); refetch();
  };

  if (loading || !data) return <Spinner />;
  const stages = data.stages || [];
  const byStage = (s) => (data.items || []).filter((l) => l.stage === s);

  return (
    <div className="animate-fade-up">
      <PageHeader title="Leads Pipeline" description="Track enquiries from first contact to won."
        action={<>
          <div className="flex rounded-lg border border-slate-300 overflow-hidden mr-2">
            <button onClick={() => setView("board")} data-testid="leads-view-board" className={`px-3 py-2 ${view === "board" ? "bg-navy text-white" : "bg-white text-slate-500"}`}><LayoutGrid size={16} /></button>
            <button onClick={() => setView("list")} data-testid="leads-view-list" className={`px-3 py-2 ${view === "list" ? "bg-navy text-white" : "bg-white text-slate-500"}`}><List size={16} /></button>
          </div>
          <Btn variant="accent" onClick={() => setOpen(true)} data-testid="create-lead-button"><Plus size={16} /> New lead</Btn>
        </>} />

      {view === "board" ? (
        <div className="flex gap-4 overflow-x-auto pb-4 amden-scroll">
          {stages.map((s) => (
            <div key={s} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(s)} data-testid={`lead-stage-${s}`}
              className="w-72 shrink-0 bg-slate-100/70 rounded-xl p-3">
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="font-semibold text-navy text-sm">{s}</span>
                <span className="text-xs text-slate-400 font-mono">{byStage(s).length}</span>
              </div>
              <div className="space-y-2 min-h-[60px]">
                {byStage(s).map((l) => (
                  <div key={l.id} draggable onDragStart={() => setDrag(l.id)} data-testid={`lead-card-${l.id}`}
                    className="amden-card p-3 cursor-grab active:cursor-grabbing group">
                    <div className="flex items-start justify-between">
                      <p className="font-medium text-navy text-sm">{l.title}</p>
                      <button onClick={() => remove(l.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{l.customer_name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <Money value={l.value} symbol={sym} className="text-sm text-navy" />
                      <span className={`text-xs px-1.5 py-0.5 rounded ${SOURCE_STYLE[l.source] || "bg-slate-100"}`}>{l.source}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="amden-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              <th className="px-4 py-3 font-semibold">Lead</th><th className="px-4 py-3 font-semibold">Customer</th>
              <th className="px-4 py-3 font-semibold">Stage</th><th className="px-4 py-3 font-semibold hidden sm:table-cell">Source</th>
              <th className="px-4 py-3 font-semibold text-right">Value</th><th></th></tr></thead>
            <tbody>
              {(data.items || []).map((l) => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50/80">
                  <td className="px-4 py-3 font-medium text-navy">{l.title}</td>
                  <td className="px-4 py-3 text-slate-600">{l.customer_name}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-accent">{l.stage}</span></td>
                  <td className="px-4 py-3 hidden sm:table-cell text-slate-500">{l.source}</td>
                  <td className="px-4 py-3 text-right"><Money value={l.value} symbol={sym} /></td>
                  <td className="px-4 py-3 text-right"><button onClick={() => remove(l.id)} className="text-slate-300 hover:text-red-600"><Trash2 size={15} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New lead</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><label className="text-sm font-medium text-slate-700">Title *</label><input value={form.title} onChange={set("title")} data-testid="lead-field-title" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" /></div>
            <div><label className="text-sm font-medium text-slate-700">Customer *</label><input value={form.customer_name} onChange={set("customer_name")} data-testid="lead-field-customer" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700">Value</label><input type="number" value={form.value} onChange={set("value")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono focus:ring-2 focus:ring-blue-500 outline-none" /></div>
              <div><label className="text-sm font-medium text-slate-700">Source</label><select value={form.source} onChange={set("source")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white">{["Manual", "Website", "Referral"].map((s) => <option key={s}>{s}</option>)}</select></div>
            </div>
          </div>
          <DialogFooter><Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn><Btn variant="accent" onClick={save} data-testid="lead-save-button">Save lead</Btn></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Leads() {
  return <PlanGate feature="leads"><LeadsInner /></PlanGate>;
}
